// Stage 97: WARP / ZERO Confusion Monitor — Settings tab.
//
// Operator-only. Renders:
//   - Enable switch + short explainer.
//   - Mode radio: off / partner / substitute.
//   - Ambient theme channel selector (8 swatches).
//   - Compact toggle.
//   - Dev tools — toggle to show the ZeroTemplate panel in preview
//     (gated by import.meta.env.DEV).
//   - "Trigger test bite" button — fires the existing /api/gadfly/bite
//     testbed and routes the response through a local adapter into the
//     preview pane (test bites do NOT emit SSE).
//   - Live preview pane — mounts BiteWidget with whatever turn is in
//     scope (live useGadflyTurn snapshot when a real turn is running,
//     otherwise the most recent test-bite synthesis).
//
// NO history, NO regenerate, NO suppress, NO export — WARP is
// presentation, not data (Stage 97 Implementation Notes #1).

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import { gadflyBite, type BiteResponse } from '../api/gadfly';
import { useGadflyTurn } from '../hooks/useGadflyTurn';
import {
  BiteWidget,
  ZeroTemplate,
  useGadflyTurnAdapter,
  WARP_CHANNELS,
  WARP_MODES,
  type WarpThemeChannel,
  type WarpMode,
  type WarpTurnState,
} from './Warp';
import './Warp/warp.css';

interface SettingsResponse {
  settings: Record<string, string>;
}

function biteResponseToWarpTurn(
  res: BiteResponse,
  turnId: string,
): WarpTurnState {
  return {
    turnId,
    bites: res.bites,
    swats: {},
    failed: res.failed,
    failureReason: res.failureReason,
    durationMs: res.durationMs,
    modelUsed: res.modelUsed,
    stripeRatio: res.stripeRatioApplied,
    generatedAt: res.generatedAt,
    // A test bite is a one-shot — there is no follow-up SSE, no swat
    // arrivals to wait for. Render the snapshot as a closed turn so
    // untagged bites are shown as implicit REJECT immediately.
    turnEnded: true,
  };
}

const CHANNEL_LABELS: Record<WarpThemeChannel, string> = {
  teal: 'Teal',
  magenta: 'Magenta',
  orange: 'Orange',
  lime: 'Lime',
  purple: 'Purple',
  gold: 'Gold',
  crimson: 'Crimson',
  cyan: 'Cyan',
};

const CHANNEL_HEX: Record<WarpThemeChannel, string> = {
  teal: '#00CED1',
  magenta: '#FF1493',
  orange: '#FF8C00',
  lime: '#00FF7F',
  purple: '#9370DB',
  gold: '#FFD700',
  crimson: '#DC143C',
  cyan: '#00FFFF',
};

const MODE_LABELS: Record<WarpMode, string> = {
  off: 'off — configured but hidden',
  partner: 'partner — show alongside DOT Large',
  substitute: 'substitute — replace DOT Large',
};

export function WarpTab({
  onChanged,
  currentConversationId,
}: {
  onChanged: () => void;
  currentConversationId?: string | null;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [mode, setMode] = useState<WarpMode>('partner');
  const [channel, setChannel] = useState<WarpThemeChannel>('teal');
  const [compact, setCompact] = useState<boolean>(false);
  const [showZeroTemplate, setShowZeroTemplate] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  // Test-bite affordance state — kept local; the test bite does not emit
  // SSE, so the live store does not receive it. The preview pane falls
  // back to this snapshot when no live turn is in flight.
  const [testTurn, setTestTurn] = useState<WarpTurnState | null>(null);
  const [testBusy, setTestBusy] = useState<boolean>(false);
  const [testErr, setTestErr] = useState<string | null>(null);

  const hostTurn = useGadflyTurn();
  const liveTurn = useGadflyTurnAdapter(hostTurn);
  const previewTurn: WarpTurnState = liveTurn.turnId
    ? liveTurn
    : (testTurn ?? liveTurn);

  const loadAll = useCallback(async () => {
    setErr(null);
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings ?? {};
      setEnabled(s.warp_enabled === 'true');
      setMode(
        s.warp_mode === 'substitute'
          ? 'substitute'
          : s.warp_mode === 'off'
            ? 'off'
            : 'partner',
      );
      const ch = s.warp_theme_channel as WarpThemeChannel | undefined;
      setChannel(ch && WARP_CHANNELS.includes(ch) ? ch : 'teal');
      setCompact(s.warp_compact === 'true');
      setShowZeroTemplate(s.warp_show_zero_template === 'true');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load settings');
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const writeSetting = useCallback(
    async (key: string, value: string) => {
      setErr(null);
      setInfo(null);
      try {
        await apiJson('PUT', '/api/settings', { [key]: value });
        onChanged();
      } catch (e) {
        setErr(e instanceof Error ? e.message : `failed to write ${key}`);
      }
    },
    [onChanged],
  );

  const toggleEnabled = useCallback(async () => {
    if (enabled === null) return;
    setBusy(true);
    const next = !enabled;
    await writeSetting('warp_enabled', next ? 'true' : 'false');
    setEnabled(next);
    setInfo(next ? 'WARP enabled.' : 'WARP disabled.');
    setBusy(false);
  }, [enabled, writeSetting]);

  const changeMode = useCallback(
    async (next: WarpMode) => {
      setMode(next);
      await writeSetting('warp_mode', next);
    },
    [writeSetting],
  );

  const changeChannel = useCallback(
    async (next: WarpThemeChannel) => {
      setChannel(next);
      await writeSetting('warp_theme_channel', next);
    },
    [writeSetting],
  );

  const toggleCompact = useCallback(async () => {
    const next = !compact;
    setCompact(next);
    await writeSetting('warp_compact', next ? 'true' : 'false');
  }, [compact, writeSetting]);

  const toggleZeroTemplate = useCallback(async () => {
    const next = !showZeroTemplate;
    setShowZeroTemplate(next);
    await writeSetting('warp_show_zero_template', next ? 'true' : 'false');
  }, [showZeroTemplate, writeSetting]);

  const runTestBite = useCallback(async () => {
    setTestBusy(true);
    setTestErr(null);
    try {
      const turnId = `warp-test-${Date.now()}`;
      const res = await gadflyBite({
        context:
          'USER: Can you remind me what the deadline was for the benchmarks?\n' +
          'ASSISTANT: I think we agreed Friday but let me check the bulletin.\n' +
          'USER: Yeah Friday — also which suite are we targeting?',
        conversationId: currentConversationId ?? `warp-test-${Date.now()}`,
        turnId,
      });
      setTestTurn(biteResponseToWarpTurn(res, turnId));
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : 'test bite failed');
    } finally {
      setTestBusy(false);
    }
  }, [currentConversationId]);

  if (enabled === null) {
    return <div className="muted">Loading…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h3 style={{ margin: 0 }}>WARP / ZERO</h3>
        <p
          style={{
            margin: '6px 0 0 0',
            color: 'var(--muted)',
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          A realtime <em>Confusion Monitor</em> for the Gadfly bite stream.
          Renders the current turn's bites as a CSS-3D warp tunnel of
          mono-text billboards in kind-coloured hues (OBSERVATION teal /
          QUESTION gold / OBJECTION crimson), with live swat transitions
          (ACCEPT brightens · DEFER desaturates · REJECT strikes through ·
          implicit REJECT fades at turn-end). Sibling to the existing
          GadflyInspector — runs alongside it, never replaces it.
        </p>
      </header>

      <section
        style={{
          padding: '10px 14px',
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            onChange={toggleEnabled}
            disabled={busy}
          />
          <strong>Enable WARP</strong>
        </label>
        <p style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
          When disabled, no chat-view widget mounts and no animation
          frames are consumed. The chat view is byte-equivalent to the
          pre-WARP baseline.
        </p>
      </section>

      {enabled ? (
        <>
          <section>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
              Mode (DOT Large coexistence)
            </h4>
            <div
              role="radiogroup"
              aria-label="WARP mode"
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              {WARP_MODES.map((m) => (
                <label
                  key={m}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="warp_mode"
                    checked={mode === m}
                    onChange={() => void changeMode(m)}
                  />
                  <code style={{ fontSize: 12, color: 'var(--fg)' }}>
                    {m}
                  </code>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {MODE_LABELS[m].split('—')[1]?.trim() ?? ''}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
              Ambient channel
            </h4>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
              }}
              role="radiogroup"
              aria-label="WARP ambient channel"
            >
              {WARP_CHANNELS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={channel === c}
                  onClick={() => void changeChannel(c)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontFamily: 'monospace',
                    background:
                      channel === c ? CHANNEL_HEX[c] : 'var(--panel-2)',
                    color: channel === c ? '#000' : 'var(--fg)',
                    border: `1px solid ${CHANNEL_HEX[c]}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: CHANNEL_HEX[c],
                      border:
                        channel === c
                          ? '1px solid #000'
                          : '1px solid rgba(255,255,255,0.25)',
                    }}
                  />
                  {CHANNEL_LABELS[c]}
                </button>
              ))}
            </div>
            <p style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
              Kind colours (OBSERVATION teal · QUESTION gold · OBJECTION
              crimson) are reserved and not selectable — the ambient
              channel governs panel chrome only.
            </p>
          </section>

          <section>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Form factor</h4>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={compact}
                onChange={() => void toggleCompact()}
              />
              <span>
                Compact (360 px panel — for narrower sidebar layouts)
              </span>
            </label>
          </section>

          {import.meta.env.DEV ? (
            <section>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
                Dev tools
              </h4>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={showZeroTemplate}
                  onChange={() => void toggleZeroTemplate()}
                />
                <span>
                  Show ZeroTemplate panel in preview (dev only — schema
                  debugging)
                </span>
              </label>
            </section>
          ) : null}

          <section>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
              Trigger test bite
            </h4>
            <p
              style={{
                margin: '0 0 8px 0',
                color: 'var(--muted)',
                fontSize: 12,
              }}
            >
              Fires the existing <code>POST /api/gadfly/bite</code>{' '}
              testbed and renders the response into the preview pane.
              Does not affect chat-view conversations.
            </p>
            <button
              type="button"
              onClick={runTestBite}
              disabled={testBusy}
              style={{
                padding: '6px 14px',
                background: 'var(--warning)',
                color: '#0a0c10',
                border: 'none',
                borderRadius: 6,
                cursor: testBusy ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {testBusy ? 'firing…' : '▸ trigger test bite'}
            </button>
            {testErr ? (
              <div
                style={{ marginTop: 8, color: 'var(--danger)', fontSize: 13 }}
              >
                {testErr}
              </div>
            ) : null}
          </section>

          <section>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Live preview</h4>
            <div
              className={`warp-root channel-${channel}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <BiteWidget
                turnState={previewTurn}
                compact={compact}
                initialEmptyState={previewTurn.turnId ? null : 'disabled'}
                subtitle={`mode: ${mode}`}
              />
              {import.meta.env.DEV && showZeroTemplate ? (
                <ZeroTemplate turnState={previewTurn} compact={compact} />
              ) : null}
            </div>
          </section>
        </>
      ) : null}

      {err ? (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>
      ) : null}
      {info ? (
        <div style={{ color: 'var(--ok)', fontSize: 13 }}>{info}</div>
      ) : null}
    </div>
  );
}
